import { useEffect } from 'react';

import { FetchResult } from '@apollo/client';
import { useFlowState } from '@betterment/js-runtime';
import {
  Box,
  Button,
  CenteredLayout,
  Heading,
  IconLink,
  Logo,
  PancakeStack,
  Separator,
  Text,
  WorkflowHeader,
} from '@betterment/samba-react';
import { Form } from 'formik';
import { useLocation, useSearchParams } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { AnySchema, boolean, date, object, string } from 'yup';

import { ErrorBoundary } from '~/b4b/components/error-boundary/ErrorBoundary';
import { GraphQLMutationForm } from '~/b4b/components/graphql-mutation-form/GraphQLMutationForm';
import { SambaAutoLink } from '~/b4b/components/SambaAutoLink';
import { urlBuilder } from '~/b4b/utils/urlBuilder';

import { IdempotencyProviderWithFeatureRegistry } from '../../../../../providers/IdempotencyProviderWithFeatureRegistry';
import { useNotifications } from '../../../reports-notifications/hooks';
import { BenefitReportCreationOption } from '../../data';
import {
  CreateBenefitReportDocument,
  CreateBenefitReportMutation,
} from '../../graphql/mutation.createReport.generated';
import { useCreateBenefitFormHelpers, useReportOptions } from '../../hooks';
import { RunReportOptions } from '../../Options';

const GenerateReportsForm = () => {
  const { options, isLoading } = useReportOptions();
  const { prepareValues, buildErrorHandlers } = useCreateBenefitFormHelpers();
  const { goToNextStep } = useFlowState();
  const { addNotification } = useNotifications();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const reportAltIdFromPathParameter = searchParams.get('report') || '';
  const initialReport = options?.find(option => option.altIdentifier === reportAltIdFromPathParameter);
  const initialReportType = initialReport?.type || '';

  useEffect(() => {
    if (!isLoading && location.hash) {
      const anchorId = location.hash.slice(1); // Remove '#'
      const anchorElement = document.getElementById(anchorId);
      if (anchorElement) {
        anchorElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location.hash, isLoading]);

  const initialValues: BenefitReportCreationOption = {
    type: initialReportType,
    startDate: '',
    endDate: '',
    year: '',
    showSsn: false,
    showOnlyMostRecentChanges: false,
    showOnlyActiveEmployees: false,
  };

  const inputTypes: Record<string, string> = {};

  const reportAttributes: Record<string, string[]> = (() => {
    const map: Record<string, string[]> = {};
    options?.forEach(option => {
      option.inputs.forEach(input => {
        if (!map[input.attribute]) {
          map[input.attribute] = [];
          inputTypes[input.attribute] = input.type;
        }
        if (!map[input.attribute]!.includes(option.type)) {
          map[input.attribute]!.push(option.type);
        }
      });
    });
    return map;
  })();

  const ReportCreationValidationSchema = (() => {
    const map: Record<string, AnySchema> = {};
    Object.keys(reportAttributes).forEach(attribute => {
      let attributeSchema;

      if (inputTypes[attribute] === 'DATE') {
        attributeSchema = date()
          .typeError(`Must be a valid date`)
          .when('type', {
            is: (val: string) => reportAttributes[attribute]?.includes(val),
            then: schema => schema.required(`Date is required`),
            otherwise: schema => schema.optional(),
          });
      } else if (inputTypes[attribute] === 'BOOLEAN') {
        attributeSchema = boolean().optional();
      } else if (inputTypes[attribute] === 'YEAR') {
        attributeSchema = string().when('type', {
          is: (val: string) => reportAttributes[attribute]?.includes(val),
          then: schema => schema.required(`Year is required`),
          otherwise: schema => schema.optional(),
        });
      } else return;
      map[attribute] = attributeSchema;
    });

    return object().shape(map);
  })();

  const onSuccess = (result: FetchResult<CreateBenefitReportMutation>) => {
    const report = result.data?.createBenefitReport?.result;
    invariant(report, `No report returned from server ${JSON.stringify(result.data)}`);

    addNotification(report.id, report.status, report.name);
    goToNextStep();
  };

  return (
    <GraphQLMutationForm
      initialValues={initialValues}
      mutation={CreateBenefitReportDocument}
      errorHandlers={buildErrorHandlers}
      validationSchema={ReportCreationValidationSchema}
      onSuccess={onSuccess}
      prepareValues={prepareValues}
      enableReinitialize={true}
    >
      {({ isSubmitting, values }) => {
        return (
          <Form id="run-report" style={{ width: '100%' }}>
            <PancakeStack css={{ maxWidth: '100%', height: '100%', position: 'relative' }}>
              <PancakeStack.Header
                css={{
                  maxWidth: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  borderBottom: 'solid',
                  borderWidth: 'thin',
                  borderColor: 'border',
                }}
              >
                <WorkflowHeader>
                  <WorkflowHeader.Leading>
                    <SambaAutoLink to={urlBuilder.dashboard()} aria-label="Back to dashboard">
                      <Logo type="b4bSignature" />
                    </SambaAutoLink>
                  </WorkflowHeader.Leading>
                  <WorkflowHeader.Title>Run report</WorkflowHeader.Title>
                  <WorkflowHeader.Trailing>
                    <IconLink name="X" label="Close" spacing="none" href={urlBuilder.reports()} />
                  </WorkflowHeader.Trailing>
                </WorkflowHeader>
              </PancakeStack.Header>
              <PancakeStack.Main>
                <CenteredLayout size="medium" css={{ py: 'space.600' }}>
                  <Heading>Run a report.</Heading>
                  <Text as="p" css={{ my: 'space.300' }}>
                    Please select the report you’d like to run, and then specify the date(s) for your report. Your
                    report will be generated as a CSV file.
                  </Text>
                  <RunReportOptions />
                </CenteredLayout>
              </PancakeStack.Main>
              <PancakeStack.Footer
                css={{ position: 'sticky', bottom: '0', width: '100%', background: 'background.surface' }}
              >
                <Separator />
                <CenteredLayout size="medium" css={{ p: 'space.200' }}>
                  <Box css={{ display: 'flex', alignItems: 'center' }}>
                    <Button disabled={isSubmitting || !values.type} type="submit">
                      {isSubmitting ? 'Submitting...' : 'Submit'}
                    </Button>
                    <GraphQLMutationForm.Failure>
                      <Text color="text.critical" css={{ ml: 'space.300' }}>
                        Couldn't generate report.
                      </Text>
                    </GraphQLMutationForm.Failure>
                  </Box>
                </CenteredLayout>
              </PancakeStack.Footer>
            </PancakeStack>
          </Form>
        );
      }}
    </GraphQLMutationForm>
  );
};

export const GenerateReportsStep = () => {
  return (
    <ErrorBoundary name="generate-reports" fallbackType="full">
      <IdempotencyProviderWithFeatureRegistry featureName="reports">
        <GenerateReportsForm />
      </IdempotencyProviderWithFeatureRegistry>
    </ErrorBoundary>
  );
};
